import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AmmDashboardApi } from '@models/RutersApi';
import { WebService } from '@services/web.service';
import {
  EditableLabelsMap,
  EditableLabelsMapResponse,
  EditableLabelTranslation,
  EditableLabelTranslationsBulkUpsert,
  TenantLabelRow,
} from '@models/EditableLabels/editable-labels.model';

@Injectable({
  providedIn: 'root',
})
export class EditableLabelsService {
  private static readonly BASE_PATH = `${environment.apiUrl}/editable-labels`;

  constructor(private webService: WebService) {}

  /** Usata da `LanguageService` per l'utente finale: non cambiare la firma. */
  getAllOverrides(): Observable<Record<string, EditableLabelsMap>> {
    return this.webService
      .httpGet<EditableLabelsMapResponse>(EditableLabelsService.BASE_PATH)
      .pipe(map((res) => res?.labelsByLanguage || {}));
  }

  /** Le chiavi che il tenant ha personalizzato, con lo stato aggregato su tutte le lingue. */
  listTenantLabels(tenantDomain: string): Observable<TenantLabelRow[]> {
    return this.webService.httpGet<TenantLabelRow[]>(this.path(AmmDashboardApi.EDITABLE_LABELS_BY_TENANT, tenantDomain));
  }

  listTranslations(key: string, tenantDomain: string): Observable<EditableLabelTranslation[]> {
    return this.webService.httpGet<EditableLabelTranslation[]>(
      this.path(AmmDashboardApi.EDITABLE_LABEL_TRANSLATIONS, tenantDomain, key),
    );
  }

  upsertTranslations(
    key: string,
    tenantDomain: string,
    translations: { language: string; value: string }[],
  ): Observable<EditableLabelTranslation[]> {
    const body: EditableLabelTranslationsBulkUpsert = { translations };
    return this.webService.httpPut<EditableLabelTranslation[]>(
      this.path(AmmDashboardApi.EDITABLE_LABEL_TRANSLATIONS, tenantDomain, key),
      body,
    );
  }

  /** Abilita o disabilita la chiave su tutte le lingue. Unico punto da cui si tocca lo stato. */
  setEnabled(key: string, tenantDomain: string, enabled: boolean): Observable<EditableLabelTranslation[]> {
    return this.webService.httpPatch<EditableLabelTranslation[]>(
      this.path(AmmDashboardApi.EDITABLE_LABEL_ENABLED, tenantDomain, key),
      { enabled },
      undefined,
    );
  }

  private path(template: string, tenantDomain: string, key?: string): string {
    const withTenant = template.replace('{tenant}', encodeURIComponent(tenantDomain));
    return key === undefined ? withTenant : withTenant.replace('{key}', encodeURIComponent(key));
  }
}
